import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useCancellationCoverApiErrorModal } from "@/hooks/api/quote/use-quote-api-error-modal";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";

import APIErrorModal from "./api-error-modal";

export default function CancelationCoverApiErrorModal({
  brand,
  // React 19 ignores `defaultProps` on function components (silently, with no
  // warning), so this default has to live here.
  disableInteraction = true,
}: {
  brand?: string;
  disableInteraction?: boolean;
}) {
  const show = useCancellationCoverApiErrorModal(brand);
  const [mounted, setMounted] = useState(false);
  const { t } = useWizardTranslation();

  useEffect(() => {
    setMounted(true);
    if (show && disableInteraction) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
    return undefined;
  }, [show, disableInteraction]);

  if (!show || !mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {disableInteraction && (
        <div
          data-testid="api-error-overlay"
          className="absolute inset-0 bg-black/40 pointer-events-auto"
        />
      )}
      <div className="relative pointer-events-auto">
        <APIErrorModal
          title={t("api_error_title", "Something went wrong")}
          description={t(
            "api_error_cancellation_description",
            "We're sorry, we can't update or retrieve your quote. Please start a new quote to continue."
          )}
          isOpenModal={show}
          isCtaPrimaryBtnEnabled={false}
          isCloseIconEnabled={false}
        />
      </div>
    </div>,
    document.body
  );
}
